import { useEffect } from 'react';

const DEFAULT_ORIGIN = 'https://www.gameplaysync.games';

const getDocumentOrigin = () => {
    if (typeof window !== 'undefined' && window.location?.origin) {
        return window.location.origin;
    }
    return DEFAULT_ORIGIN;
};

const toArray = (value) => {
    if (!value) return [];
    return Array.isArray(value) ? value.filter(Boolean) : [value];
};

export default function SeoManager({
    title,
    description,
    imageUrl,
    pageUrl,
    canonicalUrl,
    omitCanonical = false,
    robots = 'index,follow',
    structuredData = null,
    additionalMeta = [],
    keywords = [],
    alternateLinks = [],
    openGraphType = 'website',
}) {
    const normalizedStructuredData = toArray(structuredData);
    const normalizedAdditionalMeta = toArray(additionalMeta);
    const normalizedKeywords = toArray(keywords)
        .flatMap((entry) => (typeof entry === 'string' ? entry.split(',') : entry))
        .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
        .filter(Boolean);
    const normalizedAlternateLinks = toArray(alternateLinks)
        .map((entry) => {
            if (typeof entry === 'string') {
                return { rel: 'alternate', href: entry };
            }
            return entry;
        })
        .filter((entry) => entry && typeof entry.href === 'string' && entry.href.trim().length > 0);
    const structuredDataKey = normalizedStructuredData.length ? JSON.stringify(normalizedStructuredData) : null;
    const additionalMetaKey = normalizedAdditionalMeta.length ? JSON.stringify(normalizedAdditionalMeta) : null;
    const keywordsKey = normalizedKeywords.length ? normalizedKeywords.join(',') : null;
    const alternateLinksKey = normalizedAlternateLinks.length ? JSON.stringify(normalizedAlternateLinks) : null;

    useEffect(() => {
        if (!title || !description) {
            console.warn('SeoManager: title and description are required');
            return;
        }

        const normalizedRobots = typeof robots === 'string'
            ? robots.trim().toLowerCase()
            : '';
        const canExposeStructuredData = !normalizedRobots.includes('noindex');

        document.head
            .querySelectorAll(
                'script[data-public-seo-shell="structured-data"], script[data-seo-manager="structured-data"]',
            )
            .forEach((node) => node.remove());

        const upsertMetaTag = (attribute, attributeValue, contentValue) => {
            if (!contentValue) return;
            const selector = `meta[${attribute}="${attributeValue}"]`;
            let element = document.querySelector(selector);
            if (!element) {
                element = document.createElement('meta');
                element.setAttribute(attribute, attributeValue);
                document.head.appendChild(element);
            }
            element.setAttribute('content', contentValue);
        };

        const removeMetaTag = (attribute, attributeValue) => {
            document
                .querySelectorAll(`meta[${attribute}="${attributeValue}"]`)
                .forEach((element) => element.remove());
        };

        const upsertLinkTag = (relValue, hrefValue, extraAttributes = {}) => {
            if (!hrefValue) return;
            const attributes = { rel: relValue, ...extraAttributes };
            const selectorParts = [`link[rel="${relValue}"]`];
            if (attributes.hreflang) {
                selectorParts.push(`[hreflang="${attributes.hreflang}"]`);
            }
            const selector = selectorParts.join('');
            let element = document.querySelector(selector);
            if (!element) {
                element = document.createElement('link');
                element.setAttribute('rel', relValue);
                if (attributes.hreflang) {
                    element.setAttribute('hreflang', attributes.hreflang);
                }
                document.head.appendChild(element);
            }
            element.setAttribute('href', hrefValue);
            Object.entries(attributes).forEach(([attributeKey, attributeValue]) => {
                if (attributeKey === 'rel') return;
                if (!attributeValue) {
                    element.removeAttribute(attributeKey);
                    return;
                }
                element.setAttribute(attributeKey, attributeValue);
            });
        };

        const removeLinkTag = (relValue) => {
            document
                .querySelectorAll(`link[rel="${relValue}"]`)
                .forEach((element) => element.remove());
        };

        const structuredNodes = [];
        const originalTitle = document.title;
        document.title = title;

        const normalizedCanonical = omitCanonical
            ? null
            : canonicalUrl
                || pageUrl
                || (typeof window !== 'undefined' ? window.location.href : null)
                || getDocumentOrigin();

        // Standard Meta Tags
        upsertMetaTag('name', 'description', description);

        if (robots) {
            upsertMetaTag('name', 'robots', normalizedRobots);
            upsertMetaTag('name', 'googlebot', normalizedRobots);
        }

        if (normalizedKeywords.length) {
            upsertMetaTag('name', 'keywords', normalizedKeywords.join(', '));
        }

        // Open Graph Tags
        upsertMetaTag('property', 'og:title', title);
        upsertMetaTag('property', 'og:description', description);
        upsertMetaTag('property', 'og:type', openGraphType || 'website');
        upsertMetaTag('property', 'og:site_name', 'GameplaySync');

        if (imageUrl) {
            upsertMetaTag('property', 'og:image', imageUrl);
            upsertMetaTag('name', 'twitter:image', imageUrl);
        } else {
            removeMetaTag('property', 'og:image');
            removeMetaTag('name', 'twitter:image');
        }

        const urlForSharing = omitCanonical ? null : normalizedCanonical || pageUrl;
        if (urlForSharing) {
            upsertMetaTag('property', 'og:url', urlForSharing);
        } else if (omitCanonical) {
            removeMetaTag('property', 'og:url');
        }

        // Twitter Card Tags
        upsertMetaTag('name', 'twitter:card', imageUrl ? 'summary_large_image' : 'summary');
        upsertMetaTag('name', 'twitter:title', title);
        upsertMetaTag('name', 'twitter:description', description);

        // Canonical link
        if (normalizedCanonical) {
            upsertLinkTag('canonical', normalizedCanonical);
        } else if (omitCanonical) {
            removeLinkTag('canonical');
        }

        normalizedAlternateLinks.forEach((linkConfig) => {
            const relValue = linkConfig.rel || 'alternate';
            const attributes = {};
            if (linkConfig.hreflang) {
                attributes.hreflang = linkConfig.hreflang;
            }
            upsertLinkTag(relValue, linkConfig.href, attributes);
        });

        normalizedAdditionalMeta.forEach((metaConfig) => {
            if (!metaConfig) return;
            const { name, property, content } = metaConfig;
            const keyAttribute = property ? 'property' : name ? 'name' : null;
            const keyValue = property || name;
            if (!keyAttribute || !keyValue || !content) {
                return;
            }
            upsertMetaTag(keyAttribute, keyValue, content);
        });

        if (canExposeStructuredData) {
            normalizedStructuredData.forEach((data) => {
                if (!data) return;
                try {
                    const script = document.createElement('script');
                    script.setAttribute('type', 'application/ld+json');
                    script.setAttribute('data-seo-manager', 'structured-data');
                    script.textContent = JSON.stringify(data);
                    document.head.appendChild(script);
                    structuredNodes.push(script);
                } catch (error) {
                    console.warn('[SeoManager] Unable to inject structured data', error);
                }
            });
        }

        return () => {
            document.title = originalTitle;
            structuredNodes.forEach((node) => {
                if (node?.parentNode) {
                    node.parentNode.removeChild(node);
                }
            });
        };
    }, [
        title,
        description,
        imageUrl,
        pageUrl,
        canonicalUrl,
        omitCanonical,
        robots,
        structuredDataKey,
        additionalMetaKey,
        keywordsKey,
        alternateLinksKey,
        normalizedStructuredData,
        normalizedAdditionalMeta,
        normalizedKeywords,
        normalizedAlternateLinks,
        openGraphType,
    ]);

    return null;
}
